import { useRef, useMemo, useState, useEffect, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";

const IS_MOBILE = typeof window !== "undefined" && window.innerWidth < 768;
const FACE_LIMIT = IS_MOBILE ? 7 : 10;

// Render a name (+ role) into a transparent canvas texture for a crisp 3D label.
function makeLabelTexture(name, role) {
  const dpr = 2;
  const pad = 24 * dpr;
  const nameFont = `600 ${34 * dpr}px Inter, system-ui, sans-serif`;
  const roleFont = `${18 * dpr}px "Courier New", monospace`;
  const roleText = (role || "").toUpperCase();

  const measure = document.createElement("canvas").getContext("2d");
  measure.font = nameFont;
  const nameW = measure.measureText(name).width;
  measure.font = roleFont;
  const roleW = roleText ? measure.measureText(roleText).width : 0;

  const w = Math.ceil(Math.max(nameW, roleW) + pad * 2);
  const h = Math.ceil((roleText ? 96 : 62) * dpr);
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d");

  const radius = 14 * dpr;
  ctx.fillStyle = "rgba(18,18,17,0.9)";
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.arcTo(w, 0, w, h, radius);
  ctx.arcTo(w, h, 0, h, radius);
  ctx.arcTo(0, h, 0, 0, radius);
  ctx.arcTo(0, 0, w, 0, radius);
  ctx.closePath();
  ctx.fill();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = nameFont;
  ctx.fillStyle = "#F4F1E8";
  ctx.fillText(name, w / 2, roleText ? h * 0.36 : h * 0.5);
  if (roleText) {
    ctx.font = roleFont;
    ctx.fillStyle = "#C14D28";
    ctx.fillText(roleText, w / 2, h * 0.72);
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return { tex, aspect: w / h };
}

// Distribute N points on a sphere (fibonacci) for an even constellation.
function spherePoints(n, radius) {
  const pts = [];
  const off = 2 / n;
  const inc = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = i * off - 1 + off / 2;
    const r = Math.sqrt(1 - y * y);
    const phi = i * inc;
    pts.push([Math.cos(phi) * r * radius, y * radius, Math.sin(phi) * r * radius]);
  }
  return pts;
}

function FaceDisc({ face, position, onOpen }) {
  const ref = useRef();
  const [hovered, setHovered] = useState(false);
  const [texture, setTexture] = useState(null);
  const labelRef = useRef();
  const url = `${API}/public/avatar/${face.user_id}`;

  const label = useMemo(
    () => makeLabelTexture(face.name, (face.roles || [])[0]),
    [face.name, face.roles]
  );
  const labelH = 0.3;
  const labelW = labelH * label.aspect;

  useEffect(() => {
    let active = true;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      url,
      (tex) => {
        if (!active) return;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = 16;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.generateMipmaps = false;
        // Aspect-cover crop so non-square avatars aren't stretched on the circle.
        const img = tex.image;
        if (img && img.width && img.height) {
          const a = img.width / img.height;
          if (a > 1) { tex.repeat.set(1 / a, 1); tex.offset.set((1 - 1 / a) / 2, 0); }
          else { tex.repeat.set(1, a); tex.offset.set(0, (1 - a) / 2); }
        }
        tex.needsUpdate = true;
        setTexture(tex);
      },
      undefined,
      () => {}
    );
    return () => { active = false; };
  }, [url]);

  // Billboard toward camera + gentle float
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.lookAt(state.camera.position);
    const t = state.clock.elapsedTime;
    ref.current.position.y = position[1] + Math.sin(t + position[0]) * 0.06;
    const s = hovered ? 1.18 : 1;
    ref.current.scale.x += (s - ref.current.scale.x) * 0.15;
    ref.current.scale.y += (s - ref.current.scale.y) * 0.15;
    ref.current.scale.z += (s - ref.current.scale.z) * 0.15;
    if (labelRef.current) {
      const target = hovered ? 1 : 0.62;
      labelRef.current.material.opacity += (target - labelRef.current.material.opacity) * 0.15;
    }
  });

  if (!texture) return null;

  return (
    <group ref={ref} position={position}>
      {/* ring */}
      <mesh>
        <ringGeometry args={[0.52, 0.58, 48]} />
        <meshBasicMaterial color="#3E3C34" side={THREE.DoubleSide} />
      </mesh>
      {/* face */}
      <mesh
        onClick={(e) => { e.stopPropagation(); onOpen(face.user_id); }}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHovered(false); document.body.style.cursor = "auto"; }}
      >
        <circleGeometry args={[0.5, 64]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>
      {/* name label */}
      <mesh position={[0, -0.78, 0.01]} ref={labelRef}>
        <planeGeometry args={[labelW, labelH]} />
        <meshBasicMaterial map={label.tex} transparent opacity={0.62} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Constellation({ faces, onOpen }) {
  const group = useRef();
  const positions = useMemo(() => spherePoints(faces.length, 2.6), [faces.length]);
  useFrame((state, delta) => {
    if (group.current) {
      group.current.rotation.y += delta * (IS_MOBILE ? 0.07 : 0.12);
      group.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.12;
    }
  });
  return (
    <group ref={group}>
      {faces.map((f, i) => (
        <Suspense key={f.user_id} fallback={null}>
          <FaceDisc face={f} position={positions[i]} onOpen={onOpen} />
        </Suspense>
      ))}
    </group>
  );
}

export function FacesHero() {
  const [pool, setPool] = useState([]);
  const [faces, setFaces] = useState([]);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const load = (attempt = 0) => {
      fetch(`${API}/public/faces?limit=30`)
        .then((r) => { if (!r.ok) throw new Error("bad status"); return r.json(); })
        .then((d) => {
          if (!active) return;
          const p = d.faces || [];
          if (p.length === 0 && attempt < 4) throw new Error("empty");
          setPool(p);
          setFaces(p.slice(0, 10));
          setReady(true);
        })
        .catch(() => {
          if (!active) return;
          if (attempt < 4) setTimeout(() => load(attempt + 1), 2500 * (attempt + 1));
          else setReady(true);
        });
    };
    load();
    return () => { active = false; };
  }, []);

  // Refresh the constellation every 9s from the pool
  useEffect(() => {
    if (pool.length <= FACE_LIMIT) return;
    const t = setInterval(() => {
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      setFaces(shuffled.slice(0, FACE_LIMIT));
    }, 9000);
    return () => clearInterval(t);
  }, [pool]);

  const open = (uid) => navigate(`/showcase/${uid}`);

  if (!ready || faces.length === 0) {
    return <div className="w-full h-full" />;
  }

  return (
    <Canvas
      camera={{ position: [0, 0, IS_MOBILE ? 5.4 : 6.2], fov: 50 }}
      dpr={[1, IS_MOBILE ? 1.5 : 2]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={1} />
      <Constellation faces={faces} onOpen={open} />
    </Canvas>
  );
}
